import React, { useState, useEffect } from 'react';
import {
  X,
  DollarSign,
  FileText,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Modal } from '@components/Dialog';
import { materialReceiptService } from '../../services/materialReceiptService';
import { invoiceTransactionTypeService } from '../../services/invoiceTransactionTypeService';
import { MaterialReceiptOrderUpdate } from '../../types/materialReceipt';
import { InvoiceTransactionType } from '../../types/invoiceTransactionType';

// ─── Types ───────────────────────────────────────────────────────

interface OrderInfo {
  order_id: number;
  order_sell_total?: number;
  customer_name?: string;
  invoice_id?: number;
  company_id?: number;
}

interface FinancialManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  receiptId: number;
  /** Orders linked to this material receipt (distinct order_id from items) */
  linkedOrders: OrderInfo[];
}

// ─── Component ───────────────────────────────────────────────────

export const FinancialManagementModal: React.FC<FinancialManagementModalProps> = ({
  isOpen,
  onClose,
  receiptId,
  linkedOrders,
}) => {
  const [orderUpdates, setOrderUpdates] = useState<MaterialReceiptOrderUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-order action state
  const [reversalLoading, setReversalLoading] = useState<Record<number, boolean>>({});
  const [invoiceLoading, setInvoiceLoading] = useState<Record<number, boolean>>({});
  const [actionError, setActionError] = useState<Record<number, string>>({});

  // Return invoice modal state
  const [invoiceModalOrderId, setInvoiceModalOrderId] = useState<number | null>(null);
  const [transactionTypes, setTransactionTypes] = useState<InvoiceTransactionType[]>([]);
  const [selectedTransactionTypeId, setSelectedTransactionTypeId] = useState<number | null>(null);
  const [ttLoading, setTtLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadOrderUpdates();
    }
  }, [isOpen, receiptId]);

  const loadOrderUpdates = async () => {
    try {
      setLoading(true);
      setError(null);
      const updates = await materialReceiptService.getOrderUpdates(receiptId);
      setOrderUpdates(updates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getUpdateForOrder = (orderId: number): MaterialReceiptOrderUpdate | undefined => {
    return orderUpdates.find(u => u.order_id === orderId);
  };

  const handleFinancialReversal = async (orderId: number) => {
    try {
      setReversalLoading(prev => ({ ...prev, [orderId]: true }));
      setActionError(prev => ({ ...prev, [orderId]: '' }));
      await materialReceiptService.applyFinancialReversal(receiptId, orderId);
      await loadOrderUpdates();
    } catch (err) {
      setActionError(prev => ({ ...prev, [orderId]: err instanceof Error ? err.message : 'Erro ao aplicar estorno' }));
    } finally {
      setReversalLoading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleOpenReturnInvoice = async (orderId: number) => {
    const update = orderUpdates.find(u => u.order_id === orderId);
    const companyId = update?.company_id;
    if (!companyId) {
      setActionError(prev => ({ ...prev, [orderId]: 'Pedido sem empresa vinculada' }));
      return;
    }

    try {
      setTtLoading(true);
      setInvoiceModalOrderId(orderId);
      // Load transaction types filtered: TransactionType=E, FinalCustomer=1, IsBillable=1, Active=1
      const allTypes = await invoiceTransactionTypeService.getByCompany(companyId);
      const filtered = allTypes.filter(t =>
        t.transaction_type === 'E' &&
        t.final_customer === true &&
        t.is_billable === true &&
        t.active !== false
      );
      setTransactionTypes(filtered);
    } catch (err) {
      setActionError(prev => ({ ...prev, [orderId]: err instanceof Error ? err.message : 'Erro ao carregar naturezas' }));
      setInvoiceModalOrderId(null);
    } finally {
      setTtLoading(false);
    }
  };

  const handleGenerateReturnInvoice = async () => {
    if (!invoiceModalOrderId || !selectedTransactionTypeId) return;
    try {
      setInvoiceLoading(prev => ({ ...prev, [invoiceModalOrderId]: true }));
      setActionError(prev => ({ ...prev, [invoiceModalOrderId]: '' }));
      await materialReceiptService.generateReturnInvoice(receiptId, invoiceModalOrderId, {
        invoice_transaction_type_id: selectedTransactionTypeId,
      });
      setInvoiceModalOrderId(null);
      setSelectedTransactionTypeId(null);
      await loadOrderUpdates();
    } catch (err) {
      setActionError(prev => ({
        ...prev,
        [invoiceModalOrderId]: err instanceof Error ? err.message : 'Erro ao gerar nota de devolução',
      }));
    } finally {
      if (invoiceModalOrderId) {
        setInvoiceLoading(prev => ({ ...prev, [invoiceModalOrderId]: false }));
      }
    }
  };

  const formatCurrency = (v?: number) =>
    v !== undefined && v !== null
      ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—';

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen && !invoiceModalOrderId} onClose={onClose} size="lg">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <DollarSign size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Gestão Financeira / Fiscal</h2>
                <p className="text-sm text-slate-500">Recebimento #{receiptId}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
              <X size={20} className="text-slate-500" />
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={28} />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              {error}
            </div>
          ) : linkedOrders.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <p>Nenhum pedido de venda vinculado a este recebimento.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Pedidos de venda com mercadorias vinculadas a esta devolução de materiais:
              </p>

              <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
                {linkedOrders.map(order => {
                  const update = getUpdateForOrder(order.order_id);
                  const isReversalDone = update?.is_financial_reversal_applied ?? false;
                  const isInvoiceDone = update?.is_return_invoice_generated ?? false;
                  const orderError = actionError[order.order_id];

                  return (
                    <div key={order.order_id} className="p-4 bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-semibold text-slate-900">
                            Pedido #{order.order_id}
                          </span>
                          {order.customer_name && (
                            <span className="ml-2 text-sm text-slate-500">
                              — {order.customer_name}
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-medium text-slate-700">
                          {formatCurrency(order.order_sell_total)}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {/* Financial Reversal Button */}
                        <Button
                          size="sm"
                          variant={isReversalDone ? 'secondary' : 'primary'}
                          disabled={isReversalDone || reversalLoading[order.order_id]}
                          icon={
                            reversalLoading[order.order_id]
                              ? <Loader2 size={14} className="animate-spin" />
                              : isReversalDone
                                ? <CheckCircle2 size={14} />
                                : <DollarSign size={14} />
                          }
                          onClick={() => handleFinancialReversal(order.order_id)}
                        >
                          {isReversalDone ? 'Estorno Aplicado' : 'Estorno Financeiro'}
                        </Button>

                        {/* Return Invoice Button */}
                        <Button
                          size="sm"
                          variant={isInvoiceDone ? 'secondary' : 'outline'}
                          disabled={isInvoiceDone || invoiceLoading[order.order_id]}
                          icon={
                            invoiceLoading[order.order_id]
                              ? <Loader2 size={14} className="animate-spin" />
                              : isInvoiceDone
                                ? <CheckCircle2 size={14} />
                                : <FileText size={14} />
                          }
                          onClick={() => handleOpenReturnInvoice(order.order_id)}
                        >
                          {isInvoiceDone ? 'NF Devolução Emitida' : 'Nota de Devolução'}
                        </Button>
                      </div>

                      {orderError && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                          <AlertTriangle size={12} />
                          {orderError}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Return Invoice Transaction Type Selection Sub-modal */}
      <Modal isOpen={!!invoiceModalOrderId} onClose={() => { setInvoiceModalOrderId(null); setSelectedTransactionTypeId(null); }} size="md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Nota Fiscal de Devolução</h3>
            <button onClick={() => { setInvoiceModalOrderId(null); setSelectedTransactionTypeId(null); }} className="p-1 hover:bg-slate-100 rounded-lg">
              <X size={20} className="text-slate-500" />
            </button>
          </div>

          <p className="text-sm text-slate-600 mb-4">
            Selecione a natureza da operação para emissão da nota de devolução referente ao Pedido #{invoiceModalOrderId}:
          </p>

          {ttLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-blue-600" size={24} />
            </div>
          ) : transactionTypes.length === 0 ? (
            <div className="text-center py-6 text-slate-400 text-sm">
              Nenhuma natureza de operação disponível para devolução.
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {transactionTypes.map(tt => (
                <label
                  key={tt.invoice_transaction_type_id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedTransactionTypeId === tt.invoice_transaction_type_id
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="transaction_type"
                    checked={selectedTransactionTypeId === tt.invoice_transaction_type_id}
                    onChange={() => setSelectedTransactionTypeId(tt.invoice_transaction_type_id)}
                    className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{tt.invoice_transaction_type_name}</p>
                    <p className="text-xs text-slate-500">Tipo: {tt.transaction_type === 'E' ? 'Entrada' : 'Saída'}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => { setInvoiceModalOrderId(null); setSelectedTransactionTypeId(null); }}>
              Cancelar
            </Button>
            <Button
              disabled={!selectedTransactionTypeId || invoiceLoading[invoiceModalOrderId ?? 0]}
              onClick={handleGenerateReturnInvoice}
              icon={invoiceLoading[invoiceModalOrderId ?? 0] ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            >
              {invoiceLoading[invoiceModalOrderId ?? 0] ? 'Gerando...' : 'Gerar Nota de Devolução'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
